import { FC, memo } from 'react';
import { useSearchParams } from 'next/navigation';
import { css } from '@bts-web/utils-style-engine';
import {
  AmountInput,
  AmountInputWrapper,
  Divider,
  SkeletonElement,
} from '../../../common';
import { useTradeForm } from './hooks/useTradeForm/useTradeForm';
import { DisplaySwitchWrapper } from '../../../common/components/DisplaySwitchWrapper';
import { BelowInputInfoTable } from '../../../common/components/BelowInputInfoTable/BelowInputInfoTable';
import { QuickSelectButtons } from './subcomponents/QuickSelectButtons/QuickSelectButtons';
import { TARGET_PRICE_KEY, VERIFY_LINK_KEY } from '../constants';
import { TradeFormProps } from './types';
import { SetUpSavingsPlanBox } from './subcomponents/SetUpSavingsPlanBox/SetUpSavingsPlanBox';
import { VerificationLinkForm } from './subcomponents/VerificationLinkForm';
import { HiddenInputs } from './subcomponents/HiddenInputs';
import { SubmitSection } from './subcomponents/SubmitSection';
import { SubmitButtonWrapper } from '../../Reusable/SubmitButtonWrapper/SubmitButtonWrapper';
import { getAppConfig } from '@bts-web/core-app-config';
import { useFeatureFlag } from '../../../statsig/sdk/provider';
import { TradeControllerVariants, TradeOperation } from '../../utils';
import { TradeAssetSelector } from '../../TradeAssets/TradeAssetSelector';
import { MainTradeFormError } from './subcomponents/MainTradeFormError';
import { MutableNonNullableAsset } from '../types';
import { LearnMoreOrderDrawerButton } from './subcomponents/LearnMoreOrderDrawerButton/LearnMoreOrderDrawerButton';
import { OrderOperation } from '../../../orders/types';
import {
  FiatWalletProvider,
  useFiatWallet,
} from '../../Fiat/Wallet/FiatWalletContext.use-client';
import { FiatFeesSection } from '../../Fiat/Wallet/FiatFeesSection';

export const dividerStyles = css({
  paddingTop: 'medium',
  marginBottom: 'small',
  borderBottomColor: 'neutrals.divider',
  borderBottomWidth: '1px',
});

const TradeFormFooter: FC<{
  baseDisabled: boolean;
  baseLoading: boolean;
  staticValues: TradeFormProps['staticValues'];
  showPoweredBy?: boolean;
  isFiatWalletEnabled?: boolean;
  amountInput?: string;
  minimumAmount?: string;
}> = ({
  baseDisabled,
  baseLoading,
  staticValues,
  showPoweredBy,
  isFiatWalletEnabled,
  amountInput,
  minimumAmount,
}) => {
  const { isLoading } = useFiatWallet();

  const disabled = baseDisabled || isLoading;

  return (
    <SubmitButtonWrapper>
      {isFiatWalletEnabled &&
        amountInput !== undefined &&
        minimumAmount !== undefined && (
          <FiatFeesSection
            amountInput={amountInput}
            currency={staticValues.currency}
            locale={staticValues.currentLocale}
            minimumAmount={minimumAmount}
          />
        )}
      <SubmitSection
        loading={baseLoading && !disabled}
        disabled={disabled}
        staticValues={staticValues}
        showPoweredBy={showPoweredBy}
      />
    </SubmitButtonWrapper>
  );
};

export const MainTradeForm: FC<TradeFormProps> = ({
  assetDetails,
  currentTradeDetails,
  locale,
  staticValues,
  isDataLoading,
  targetAssetData,
  overrideOperationWithVariant,
  openBuyTab,
}) => {
  const { formState, handlers } = useTradeForm({
    assetDetails,
    targetAssetData,
    currentTradeDetails,
    staticValues,
  });

  const searchParams = useSearchParams();

  const verifyLink = searchParams?.get(VERIFY_LINK_KEY);

  const { savingsPlansFrequencySelection } = getAppConfig();

  const feature_enableFiatTransfers = useFeatureFlag(
    'feature_enableFiatTransfers',
  );

  const isFiatWalletEnabled =
    staticValues.transactionType === 'buy' && feature_enableFiatTransfers;

  const allowAssetSelection = (
    ['buy', 'sell', 'swap', 'stake', 'unstake'] as TradeControllerVariants[]
  ).includes(staticValues.transactionType);

  const hasOrderTargetPrice =
    searchParams.has(TARGET_PRICE_KEY) &&
    staticValues.transactionType === TradeOperation.order;

  const isSellOrderPriceScreen =
    !searchParams.has(TARGET_PRICE_KEY) &&
    staticValues.orderOperation === OrderOperation.SELL &&
    staticValues.transactionType === TradeOperation.order;

  if (verifyLink) {
    return (
      <VerificationLinkForm
        isDataLoading={isDataLoading}
        formState={formState}
        staticValues={staticValues}
        currentTradeDetails={currentTradeDetails}
        verifyLink={verifyLink}
      />
    );
  }

  const submitDisabled =
    formState.inputValue?.length === 0 ||
    formState.formHasAnyErrors ||
    formState.allButtonsDisabled ||
    Number(formState.inputValue.replace(',', '.')) === 0;

  const inputTitleByVariant: Partial<Record<TradeControllerVariants, string>> =
    {
      order: hasOrderTargetPrice
        ? staticValues.translations.targetPrice
        : staticValues.orderOperation === OrderOperation.SELL
          ? staticValues.translations.sellAmount
          : staticValues.translations.buyAmount,
    };

  const showQuickSelectButtons =
    isSellOrderPriceScreen ||
    (staticValues.transactionType !== TradeOperation.order
      ? true
      : searchParams.has(TARGET_PRICE_KEY));

  const isSellTrade = staticValues.transactionType === 'sell';

  return (
    <form
      onSubmit={handlers.onFormSubmit}
      className={css({ flex: 1, display: 'flex', flexDir: 'column' })}
      data-testid="trade-form"
    >
      <HiddenInputs formState={formState} />

      <AmountInputWrapper
        inputContextData={formState.inputContextData}
        switcherElementSlot={
          isSellTrade ? (
            <DisplaySwitchWrapper
              parentValue={formState.displayType}
              items={[
                {
                  label: staticValues.translatedValues.selectorLabels.fiat,
                  value: 'FIAT',
                },
                {
                  label: staticValues.translatedValues.selectorLabels.asset,
                  value: 'ASSET',
                },
              ]}
              onChange={(value) => {
                handlers.onChangeDisplayType(value);
              }}
            />
          ) : null
        }
        learnMoreElementSlot={
          hasOrderTargetPrice ? <LearnMoreOrderDrawerButton /> : null
        }
        title={
          inputTitleByVariant[
            staticValues.transactionType as TradeControllerVariants
          ]
        }
      >
        {({ inputRef, modifiedBlur }) => {
          return (
            <AmountInput
              ref={inputRef}
              placeholder={formState.inputPlaceholder}
              value={formState.inputValue}
              disabled={formState.allButtonsDisabled}
              onBlur={modifiedBlur}
              onChange={(e) => handlers.onTradeInputChange(e.target.value)}
              smaller={staticValues.transactionType === 'sell'}
            />
          );
        }}
      </AmountInputWrapper>

      {!hasOrderTargetPrice && (
        <div
          className={css({
            display: 'flex',
            flexDirection: 'column',
            gap: 'extra_small_2',
            pt: 'small',
            pb: 'medium',
          })}
        >
          <BelowInputInfoTable
            items={[
              {
                value: isDataLoading ? (
                  <SkeletonElement width="100" height="20" borderRadius="10%" />
                ) : (
                  formState.convertedSummary
                ),
              },
            ]}
          />
        </div>
      )}

      {showQuickSelectButtons && (
        <QuickSelectButtons
          items={currentTradeDetails.quickSelectTradeItems}
          onQuickSelectButtonClick={handlers.onQuickSelectButtonClick}
          disabled={formState.allButtonsDisabled}
          displayType={formState.displayType}
          inputValue={formState.inputValue}
        />
      )}

      <Divider className={dividerStyles} />

      <MainTradeFormError
        locale={locale}
        transactionType={staticValues.transactionType}
        errors={formState.errors}
        minimumTradeAmount={currentTradeDetails.minimumTradeAmount}
        openBuyTab={openBuyTab}
        assetDetails={assetDetails}
      />

      {staticValues.transactionType === 'savings' && (
        <div className={css({ paddingTop: 'medium' })}>
          <SetUpSavingsPlanBox
            translations={{
              setUpSavingsPlanBoxTitle:
                staticValues.translations.setUpSavingsPlanBoxTitle,
              setUpSavingsPlanBoxDescription:
                staticValues.translations.setUpSavingsPlanBoxDescription,
              frequency: staticValues.translations.frequency,
              startsAt: staticValues.translations.startsAt,
              monthly: staticValues.translations.MONTHLY,
              biweekly: staticValues.translations.BIWEEKLY,
              weekly: staticValues.translations.WEEKLY,
              daily: staticValues.translations.DAILY,
              ok: staticValues.translations.ok,
              cancel: staticValues.translations.cancel,
              nextRecurrence: staticValues.translations.nextRecurrence,
            }}
            locale={locale}
            recurrenceDate={formState.savingsPlanRecurringDate}
            onChangeDate={handlers.setSavingsPlanRecurringDate}
            showFrequencyDropdown={
              (savingsPlansFrequencySelection?.length ?? 0) > 0
            }
            frequencyOptions={savingsPlansFrequencySelection}
            frequencyValue={formState.savingsPlanFrequency}
            onFrequencyChange={handlers.setSavingsPlanFrequency}
          />
        </div>
      )}

      <FiatWalletProvider enabled={isFiatWalletEnabled}>
        {allowAssetSelection && (
          <TradeAssetSelector
            locale={locale}
            selectedAssetData={
              assetDetails as unknown as MutableNonNullableAsset
            }
            overrideOperationWithVariant={overrideOperationWithVariant}
            targetAssetData={targetAssetData}
            amountInput={formState.inputValue}
          />
        )}

        <TradeFormFooter
          baseLoading={formState.isLoading || isDataLoading}
          baseDisabled={submitDisabled}
          staticValues={staticValues}
          showPoweredBy={!isFiatWalletEnabled}
          isFiatWalletEnabled={isFiatWalletEnabled}
          amountInput={formState.inputValue}
          minimumAmount={currentTradeDetails.minimumTradeAmount.FIAT?.value}
        />
      </FiatWalletProvider>
    </form>
  );
};

export default memo(MainTradeForm);
